import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { firstValueFrom } from 'rxjs';

/**
 * Refreshes shop status (is_ordering_active, is_open_now, are_call_actions_active)
 * from lightweight API endpoints and merges into clientData + sessionStorage.
 * Aligns with iOS loadShopStatus() behavior.
 */
@Injectable({
  providedIn: 'root'
})
export class ShopStatusService {

  constructor(private apiService: ApiService) {}

  /**
   * Fetches current shop status (ordering active, working hours, call actions)
   * and merges into clientData in sessionStorage. Returns the merged clientData
   * so callers can update their local reference. Individual API failures are
   * ignored so we still merge whatever succeeded.
   */
  async refreshShopStatus(shopId: number): Promise<any> {
    const clientDataString = sessionStorage.getItem('clientData');
    if (!clientDataString) {
      return null;
    }
    let clientData: any;
    try {
      clientData = JSON.parse(clientDataString);
    } catch {
      return null;
    }

    const orderingPromise = firstValueFrom(this.apiService.checkIsOrderingActive(shopId)).catch(() => null);
    const orderInfoPromise = firstValueFrom(this.apiService.getOrderInfo(shopId)).catch(() => null);
    const callActionsPromise = firstValueFrom(this.apiService.checkAreCallActionsActive(shopId)).catch(() => null);

    const [orderingResponse, orderInfo, callActionsResponse] = await Promise.all([
      orderingPromise,
      orderInfoPromise,
      callActionsPromise
    ]);

    if (orderingResponse && (orderingResponse.is_ordering_active !== undefined || orderingResponse.is_open_now !== undefined)) {
      if (orderingResponse.is_ordering_active !== undefined) {
        clientData.is_ordering_active = orderingResponse.is_ordering_active;
      }
      if (orderingResponse.is_open_now !== undefined) {
        clientData.is_open_now = orderingResponse.is_open_now;
      }
    }
    if (orderInfo) {
      if (orderInfo.is_ordering_active !== undefined) {
        clientData.is_ordering_active = orderInfo.is_ordering_active;
      }
      if (orderInfo.is_open_now !== undefined) {
        clientData.is_open_now = orderInfo.is_open_now;
      }
    }
    if (callActionsResponse && callActionsResponse.are_call_actions_active !== undefined) {
      clientData.are_call_actions_active = callActionsResponse.are_call_actions_active;
    }

    sessionStorage.setItem('clientData', JSON.stringify(clientData));
    return clientData;
  }
}
